/** Constructing configuration relationships to connections
 *
 * Overview:
 *    This module defines inputs that tell you how to display inputs for
 *  AiService related configurations. As well as some utility functions to
 *  process those inputs.
 *
 *    Some care has been taken to make it less error prone when defining the
 *  inputs that relate to a specific configuration. These include: Enforcing
 *  keys that exist for a given configuration by the connection type used,
 *  that inputs have the correct associated key (value=keyof config), and
 *  autocorrect should be fairly useful whenever you need to define more inputs.
 *
 *
 */
const api_key = {
    label: "API Key",
    value: "api_key",
    isSecret: true
};
const endpoint = {
    label: "Endpoint",
    value: "endpoint"
};
const endpoint_route = {
    label: "Endpoint Route",
    value: "endpoint_route"
};
const huggingface_api_key = {
    label: "API Key",
    value: "huggingface_api_key",
    isOptional: true,
    isSecret: true
};
const org_id = {
    label: "Org ID",
    value: "org_id"
};
const api_token = {
    label: "API Token",
    value: "api_token",
    isSecret: true
};
const function_id = {
    label: "Function ID",
    value: "function_id",
    isOptional: true
};
const version_id = {
    label: "Version ID",
    value: "version_id",
    isOptional: true
};
const uri = {
    label: "URI",
    value: "uri",
    isHidden: true,
    isOptional: true
};
const usage = {
    label: "Usage",
    value: "usage",
    isMulti: true,
    isHidden: true,
    isOptional: true,
    options: [
        { label: "Completion", value: "Completion" },
        { label: "CodeGen", value: "CodeGen" },
        { label: "Embedding", value: "Embedding" },
        { label: "Reranking", value: "Reranking" },
        { label: "FineTuning", value: "FineTuning" }
    ]
};
const transformer_type = {
    label: "Transformer Type",
    value: "transformer_type",
    isOptional: true,
    isHidden: true,
    options: [
        { label: "Llama", value: "llama", default: true },
        { label: "Mistral", value: "mistral" }
    ]
};
export const connectionConfigSettings = {
    OPEN_AI: {
        value: "OPEN_AI",
        label: "OpenAI",
        settings: {
            api_key,
            base_url: { label: "Base URL", value: "base_url", isOptional: true }
        },
        buildConfig: (settings) => {
            const type = "OPEN_AI";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    AZURE_OPEN_AI: {
        value: "AZURE_OPEN_AI",
        label: "Azure OpenAI",
        settings: {
            api_key,
            base_url: { label: "Base URL", value: "base_url", isOptional: true },
            api_version: { label: "API Version", value: "api_version", isOptional: true },
            chat_model: { label: "Chat Model", value: "chat_model", isOptional: true, isHidden: true },
            embedding_model: { label: "Embedding Model", value: "embedding_model", isOptional: true, isHidden: true },
            code_model: { label: "Code Model", value: "code_model", isOptional: true, isHidden: true }
        },
        buildConfig: (settings) => {
            const type = "AZURE_OPEN_AI";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    ANTHROPIC: {
        value: "ANTHROPIC",
        label: "Anthropic",
        settings: {
            api_key,
            base_url: { label: "Base URL", value: "base_url", isOptional: true }
        },
        buildConfig: (settings) => {
            const type = "ANTHROPIC";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    VERTEX_AI_GEMINI: {
        value: "VERTEX_AI_GEMINI",
        label: "Vertex AI Gemini",
        settings: [],
        buildConfig: () => {
            const type = "VERTEX_AI_GEMINI";
            const config = {
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    VERTEX_AI: {
        value: "VERTEX_AI",
        label: "VERTEX AI",
        disabled: true,
        settings: [],
        buildConfig: () => {
            const type = "VERTEX_AI";
            const config = {
                type
            };
            return ["COMPLETE", config];
        }
    },
    BEDROCK: {
        value: "BEDROCK",
        label: "Bedrock",
        disabled: true,
        settings: [],
        buildConfig: () => {
            const type = "BEDROCK";
            const config = {
                type
            };
            return ["COMPLETE", config];
        }
    },
    LLAMA_CPP_AVX2: {
        value: "LLAMA_CPP_AVX2",
        label: "Llama-CPP-AVX2",
        disabled: true,
        settings: {},
        buildConfig: () => {
            const type = "LLAMA_CPP_AVX2";
            const config = {
                type
            };
            return ["COMPLETE", config];
        }
    },
    NVCF_HF: {
        value: "NVCF_HF",
        label: "NVIDIA - HuggingFace",
        disabled: true,
        settings: {
            org_id,
            api_token,
            function_id,
            version_id,
            huggingface_api_key
        },
        buildConfig: (settings) => {
            const type = "NVCF_HF";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    NVCF_NIM: {
        value: "NVCF_NIM",
        label: "NVIDIA - NIM",
        disabled: true,
        settings: {
            org_id,
            api_token,
            function_id,
            version_id,
            uri,
            usage,
            transformer_type
        },
        buildConfig: (settings) => {
            const type = "NVCF_NIM";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    NIM_LLM: {
        value: "NIM_LLM",
        label: "NIM LLM",
        disabled: false,
        settings: {
            endpoint,
            uri: {
                label: "URI",
                value: "uri"
            },
            usage,
            transformer_type
        },
        buildConfig: (settings) => {
            const type = "NIM_LLM";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    NVCF_FT: {
        value: "NVCF_FT",
        label: "NVIDIA - FineTuning",
        disabled: true,
        settings: {
            huggingface_api_key
        },
        buildConfig: (settings) => {
            const type = "NVCF_FT";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    },
    DGX: {
        value: "DGX",
        label: "DGX",
        settings: {
            endpoint,
            endpoint_route,
            huggingface_api_key
        },
        buildConfig: (settings) => {
            const type = "DGX";
            const config = {
                ...settings,
                type
            };
            if (isConnectionConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        }
    }
};
export function isConnectionConfigFilled(type, config) {
    const configSettings = connectionConfigSettings[type].settings;
    const inputs = Object.values(configSettings);
    return inputs.map((input) => Boolean(config[input.value] || input.isOptional)).every((x) => x);
}
export const modelConfigSettings = {
    CONTENT_GENERATION: {
        value: "CONTENT_GENERATION",
        label: "CONTENT_GENERATION",
        settings: {
            uri,
            usage,
            transformer_type,
            loras: {
                /* MB: We need some way to handle uploading a lot of this data */
                value: "loras",
                label: "LoRA Weights",
                isOptional: true,
                isHidden: true
            }
        },
        buildConfig: (settings) => {
            const type = "CONTENT_GENERATION";
            const config = {
                ...settings,
                type
            };
            if (isModelConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        },
        getDefault: () => ({
            type: "CONTENT_GENERATION",
            uri: ""
        })
    },
    EMBEDDING: {
        value: "EMBEDDING",
        label: "EMBEDDING",
        settings: {
            uri,
            usage
        },
        buildConfig: (settings) => {
            const type = "EMBEDDING";
            const config = {
                ...settings,
                type,
                embedding_type: "mistral"
            };
            if (isModelConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        },
        getDefault: () => ({
            type: "EMBEDDING",
            embedding_type: "mistral",
            uri: ""
        })
    },
    RERANKING: {
        value: "RERANKING",
        label: "RERANKING",
        settings: {
            uri,
            usage
        },
        buildConfig: (settings) => {
            const type = "RERANKING";
            const config = {
                ...settings,
                type
            };
            if (isModelConfigFilled(type, config)) {
                return ["COMPLETE", config];
            }
            return ["INCOMPLETE", config];
        },
        getDefault: () => ({
            type: "RERANKING",
            uri: ""
        })
    }
};
export function isModelConfigFilled(type, config) {
    const configSettings = modelConfigSettings[type].settings;
    const inputs = Object.values(configSettings);
    return inputs.map((input) => Boolean(config[input.value] || input.isOptional)).every((x) => x);
}
